import { useState, useEffect } from 'react';
import { QueryClient, QueryClientProvider, MutationCache } from '@tanstack/react-query';
import { AppShell } from './components/layout/app-shell';
import { PlaceholderPage } from './components/shared/placeholder-page';
import { ErrorBoundary } from './components/shared/error-boundary';
import { Toaster } from './components/ui/toaster';
import { toast } from './stores/toast-store';
import { LoginPage } from './pages/auth/login';
import { SignupPage } from './pages/auth/signup';
import { AdminLoginPage } from './pages/auth/admin-login';
import { ForgotPasswordPage } from './pages/auth/forgot-password';
import { ResetPasswordPage } from './pages/auth/reset-password';
import { PendingActivationPage } from './pages/dashboard/pending-activation';
import { OnboardingPage } from './pages/dashboard/onboarding-page';
import { RegistrationWizard } from './pages/dashboard/registration-wizard';
import { AddCompanyPage } from './pages/dashboard/add-company-page';
import { HomePage } from './pages/dashboard/home-page';
import { UsersPage } from './pages/dashboard/users-page';
import { UserFormPage } from './pages/dashboard/user-form-page';
import { RolesPage } from './pages/dashboard/roles-page';
import { RoleEditorPage } from './pages/dashboard/role-editor-page';
import { CollectionsPage } from './pages/dashboard/collections-page';
import { PayablesPage } from './pages/dashboard/payables-page';
import { SettingsPage } from './pages/dashboard/settings-page';
import { TwoFactorSetupPage } from './pages/dashboard/two-factor-setup';
import { PosManagementPage } from './pages/dashboard/pos-management-page';
import { OnlineStorePage } from './pages/dashboard/online-store-page';
import { PurchaseOrderCreatePage } from './pages/purchasing/purchase-order-create';
import { PurchaseOrderEditPage } from './pages/purchasing/purchase-order-edit';
import { PurchaseOrderPrintPage } from './pages/purchasing/purchase-order-print';
import { PackingConsolidatedPrintPage } from './pages/purchasing/packing-consolidated-print';
import { OrderReceiptPrintPage } from './pages/reports/order-receipt-print';
import { SaleOrderEditPage } from './pages/purchasing/sale-order-edit';
import { SaleOrderPrintPage } from './pages/purchasing/sale-order-print';
import { ProductCreatePage } from './pages/products/product-create';
import { AddLocationWizard } from './pages/products/add-location-wizard';
import { TimezoneSync } from './components/shared/timezone-sync';
import { ProductEditPage } from './pages/products/product-edit';
import { ImportHistoryPage } from './pages/products/import-history';
import { ReportsPage } from './pages/reports';
import { StatementsPage } from './pages/dashboard/statements-page';
import { EslPage } from './pages/esl/esl-page';
import { ReconciliationPage } from './pages/dashboard/reconciliation-page';
import { FundingTransactionsPage } from './pages/dashboard/funding-transactions-page';
import { PayByLinkPage } from './pages/dashboard/pay-by-link-page';
import { AdminDashboardPage } from './pages/admin/admin-dashboard';
import { AdminTenantDetailPage } from './pages/admin/admin-tenant-detail';
import { AdminSyncPage } from './pages/admin/admin-sync';
import { AdminBatchProcessPage } from './pages/admin/admin-batch-process';
import { AdminGlobalSettingsPage } from './pages/admin/admin-global-settings';
import { AdminStatementsUploadPage } from './pages/admin/admin-statements-upload';
import { AdminCommonProductImagesPage } from './pages/admin/admin-common-product-images';
import { AdminAutoPoPage } from './pages/admin/admin-auto-po';
import { AdminCcFundingDriveSyncPage } from './pages/admin/admin-cc-funding-drive-sync';
import { AdminPaymentReconPage } from './pages/admin/admin-payment-recon';
import { AdminMailingReportPage } from './pages/admin/admin-mailing-report';
import { AdminPhoneNumbersPage } from './pages/admin/admin-phone-numbers';
import { AdminCustomerImportPage } from './pages/admin/admin-customer-import';
import { AdminPosAppVersionsPage } from './pages/admin/admin-pos-app-versions';
import { AdminCampaignUsagePage } from './pages/admin/admin-campaign-usage';
import { AdminPaymentProvidersPage } from './pages/admin/admin-payment-providers';
import { AdminFeesPricingPage } from './pages/admin/admin-fees-pricing';
import type { PermissionKey } from '@supernova/shared';
import { useAuthCheck } from './hooks/use-auth';
import { usePermissions } from '@/hooks/use-permissions';
import { useAuthStore } from './stores/auth-store';
import { useReportsEnabled } from './hooks/use-reports-access';
import { useModules } from './hooks/use-modules';
import { useLocations } from './hooks/use-locations';
import { useOnboarding } from './hooks/use-onboarding';
import { useLocationStore } from './stores/location-store';
import { DatePickerDemo } from './pages/dev/date-picker-demo';

function extractErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'error' in error) {
    const err = (error as { error: { message?: string } }).error;
    if (err.message) return err.message;
  }
  if (error instanceof Error) return error.message;
  return 'Something went wrong. Please try again.';
}

const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    // Global mutation error handler — shows a toast for any mutation that fails,
    // UNLESS the mutation sets meta.suppressGlobalError = true (for mutations that
    // handle their own errors inline, like login and signup).
    onError: (error: unknown, _variables, _context, mutation) => {
      if ((mutation.options.meta as Record<string, unknown> | undefined)?.suppressGlobalError) return;
      toast.error(extractErrorMessage(error));
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

function useHashRoute() {
  const [hash, setHash] = useState(window.location.hash.slice(1).split('?')[0] || '/');
  useEffect(() => {
    const onHashChange = () => setHash(window.location.hash.slice(1).split('?')[0] || '/');
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);
  return hash;
}

function AdminRouter() {
  const path = useHashRoute();

  if (path === '/admin' || path === '/') return <AdminDashboardPage />;

  const tenantMatch = path.match(/^\/admin\/tenants\/([^/]+)$/);
  if (tenantMatch) {
    return <AdminTenantDetailPage tenantId={tenantMatch[1]} />;
  }

  // Sync tab — tenant picker or tenant-specific sync page
  if (path === '/admin/sync') return <AdminSyncPage />;
  const syncTenantMatch = path.match(/^\/admin\/sync\/([^/]+)$/);
  if (syncTenantMatch) return <AdminSyncPage tenantId={syncTenantMatch[1]} />;

  if (path === '/admin/batch-process') return <AdminBatchProcessPage />;

  if (path === '/admin/settings') return <AdminGlobalSettingsPage />;
  if (path === '/admin/statements-upload') return <AdminStatementsUploadPage />;

  if (path === '/admin/common-images') return <AdminCommonProductImagesPage />;

  if (path === '/admin/auto-po') return <AdminAutoPoPage />;

  if (path === '/admin/cc-funding-drive-sync') return <AdminCcFundingDriveSyncPage />;
  if (path === '/admin/payment-recon') return <AdminPaymentReconPage />;

  if (path === '/admin/mailing-report') return <AdminMailingReportPage />;

  if (path === '/admin/phone-numbers') return <AdminPhoneNumbersPage />;

  if (path === '/admin/customer-import') return <AdminCustomerImportPage />;
  if (path === '/admin/pos-app-versions') return <AdminPosAppVersionsPage />;
  if (path === '/admin/pos-app-versions/mobile') return <AdminPosAppVersionsPage app="mobile" />;
  if (path === '/admin/pos-app-versions/cds') return <AdminPosAppVersionsPage app="cds" />;

  if (path === '/admin/campaign-usage') return <AdminCampaignUsagePage />;

  if (path === '/admin/payment-providers') return <AdminPaymentProvidersPage />;

  if (path === '/admin/fees') return <AdminFeesPricingPage />;
  const feesTenantMatch = path.match(/^\/admin\/fees\/([^/]+)$/);
  if (feesTenantMatch) return <AdminFeesPricingPage tenantId={feesTenantMatch[1]} />;

  return <PlaceholderPage title="Page Not Found" description="The page you're looking for doesn't exist." />;
}

function InactiveTenantRouter() {
  const path = useHashRoute();
  const { tenant } = useAuthStore();
  const { selectedLocationId } = useLocationStore();
  const { data: modulesData, isLoading: modulesLoading } = useModules(selectedLocationId);
  const modules = modulesData?.data ?? [];
  const hasActiveModules = modules.some((m) => m.isActive);

  if (modulesLoading) {
    return <div className="flex h-64 items-center justify-center text-muted-foreground">Loading...</div>;
  }

  // Stage 1: No modules selected yet → show module selection (settings accessible here)
  if (!hasActiveModules) {
    if (path === '/settings') return <SettingsPage />;
    return <OnboardingPage />;
  }

  // Stage 2: Modules selected but onboarding not completed → registration wizard (forced)
  //
  // Skipped for an ESL-only client. The wizard collects payments onboarding —
  // EIN, representative identity, ownership, bank account — and an external-POS
  // or standalone shelf-label client is not a payments merchant, so there is
  // nothing to ask and no reason to hold that data. They go straight from
  // picking the module to waiting on the super admin's gate.
  if (!tenant?.onboardingCompleted && tenant?.type !== 'esl') {
    return <RegistrationWizard locationId={selectedLocationId} />;
  }

  // Stage 3: Onboarding completed, waiting for super admin activation (settings accessible)
  if (path === '/settings') return <SettingsPage />;
  return <PendingActivationPage />;
}

/**
 * Handles routing when the currently-selected location exists in the DB but is
 * not yet active (isActive: false).  Two distinct sub-states:
 *
 *   • Not yet submitted  → module selection flow (OnboardingPage → RegistrationWizard)
 *   • Submitted, waiting → PendingActivationPage
 *
 * This replaces the old inline "always show PendingActivationPage" gate so that
 * users who navigate away mid-registration are returned to module selection (with
 * their previous module choices intact) instead of a dead-end pending page.
 */
function InactiveLocationRouter({ locationId }: { locationId: string }) {
  const path = useHashRoute();
  const { data: onboardingRes, isLoading } = useOnboarding(locationId);

  if (isLoading) {
    return <div className="flex h-64 items-center justify-center text-muted-foreground">Loading...</div>;
  }

  const isSubmitted = onboardingRes?.data?.isSubmitted ?? false;

  // Settings are accessible in all sub-states
  if (path === '/settings') return <SettingsPage />;

  // Once submitted, show pending activation page until super admin activates
  if (isSubmitted) {
    return <PendingActivationPage locationId={locationId} />;
  }

  // Not yet submitted: registration wizard is reachable only via the
  // "Continue Registration" button on the module-selection page.
  const registerMatch = path.match(/^\/register\/([^/]+)$/);
  if (registerMatch) {
    return <RegistrationWizard locationId={registerMatch[1]} />;
  }

  // Default (any other path, including / and /modules): show module selection.
  // OnboardingPage reads selectedLocationId from the store; since this component
  // only renders when that location is the selected one, it always queries the
  // correct location's modules.  Previously-selected modules are preserved in the
  // DB and will show as checked so the user can review before continuing.
  return <OnboardingPage />;
}

function TenantRouter() {
  const path = useHashRoute();
  const { tenant } = useAuthStore();
  const { selectedLocationId } = useLocationStore();
  const { data: locationsData } = useLocations();
  // Same module gate the sidebar uses for the Statements entry — checked here
  // too so the page can't be reached by typing the URL.
  const { data: routerModulesData } = useModules(selectedLocationId);
  const statementsEnabled =
    (routerModulesData?.data ?? []).find((m: any) => m.moduleName === 'statements')?.superAdminEnabled === true;
  // Reports is on by default; the super admin can switch it off per business or
  // location. Guarded here as well as in the sidebar so the URL can't reach it.
  const reportsEnabled = useReportsEnabled();
  // Shelf labels are two-phase: the super admin opens the gate, then the
  // merchant switches the module on, so isActive is the field that decides.
  // While the platform release switch is off the API omits the module entirely,
  // so this reads false without a second check here.
  const eslEnabled = (routerModulesData?.data ?? []).find((m: any) => m.moduleName === 'esl')?.isActive === true;
  const allLocations = locationsData?.data ?? [];
  const selectedLocation = allLocations.find((l: any) => l.id === selectedLocationId);
  // Each tab opens with any key of its section, matching the sidebar, so a
  // hidden page can't be reached by URL either. Each falls back to Home.
  // Settings, Online Store and the like stay tied to the role.
  const perms = usePermissions();
  const isAdmin = perms.atLeast('admin');
  const isOwner = perms.atLeast('owner');

  // If tenant is not active, show onboarding or pending activation flow
  if (tenant && !tenant.isActive) {
    return <InactiveTenantRouter />;
  }

  // Add Company is always accessible — user can start a new company at any time,
  // even while an existing location is still pending activation.
  if (path === '/add-company') return <AddCompanyPage />;

  // If the selected location is inactive, delegate all routing to InactiveLocationRouter.
  // It checks whether the registration has been submitted and shows either:
  //   • Module selection + registration wizard (not yet submitted)
  //   • Pending activation page (submitted, waiting for super admin)
  if (selectedLocation && !selectedLocation.isActive) {
    return <InactiveLocationRouter locationId={selectedLocationId} />;
  }

  // Post-creation window: useLocations() may still be stale and the newly-created
  // location hasn't appeared in allLocations yet.  Serve /modules and /register/:id
  // directly so the user can progress through the onboarding flow immediately after
  // Add Company completes, without waiting for the locations list to re-fetch.
  if (path === '/modules') return <OnboardingPage />;
  const newLocationRegisterMatch = path.match(/^\/register\/([^/]+)$/);
  if (newLocationRegisterMatch) return <RegistrationWizard locationId={newLocationRegisterMatch[1]} />;

  // Default landing page
  if (path === '/') {
    return <HomePage />;
  }

  // Role-gated routes
  // User Management and its sub-pages; each page also checks its own access.
  if (path === '/users' || path.startsWith('/users/')) {
    if (!perms.canOpen('users')) return <HomePage />;
    if (path === '/users') return <UsersPage />;
    if (path === '/users/new') return <UserFormPage />;
    const userEditMatch = path.match(/^\/users\/([^/]+)\/edit$/);
    if (userEditMatch) return <UserFormPage key={userEditMatch[1]} userId={userEditMatch[1]} />;
    if (path === '/users/roles') return <RolesPage />;
    const roleMatch = path.match(/^\/users\/roles\/([^/]+)$/);
    if (roleMatch) return <RoleEditorPage key={roleMatch[1]} param={roleMatch[1]} />;
    return <UsersPage />;
  }
  if (path === '/settings') {
    if (!isAdmin) return <HomePage />;
    return <SettingsPage />;
  }
  if (path === '/settings/2fa') {
    return <TwoFactorSetupPage />;
  }
  if (path === '/receive-payments') return perms.canOpen('receivePayments') ? <CollectionsPage /> : <HomePage />;
  if (path === '/send-payments') return perms.canOpen('sendPayments') ? <PayablesPage /> : <HomePage />;
  if (path === '/online-store') {
    // Owner-only surface. The page itself handles the super-admin gate (shows a
    // "not enabled" state if the backend reports the module gate is closed).
    if (!isOwner) return <HomePage />;
    return <OnlineStorePage />;
  }
  if (path.startsWith('/pos-management') && !perms.canOpen('posManagement')) return <HomePage />;
  if (path === '/pos-management') return <PosManagementPage />;
  // Sub-routes of POS Management, each with a key of the screen it belongs to.
  const routeKeys = (...keys: PermissionKey[]) => perms.hasAny(...keys);
  if (path === '/pos-management/products/create') {
    return routeKeys('products.add') ? <ProductCreatePage /> : <HomePage />;
  }
  if (path === '/pos-management/products/import-history') {
    return routeKeys('products.import') ? <ImportHistoryPage /> : <HomePage />;
  }
  if (path === '/locations/new') return <AddLocationWizard />;
  const productEditMatch = path.match(/^\/pos-management\/products\/([^/]+)\/edit$/);
  if (productEditMatch) {
    return routeKeys('products.view') ? <ProductEditPage productId={productEditMatch[1]} /> : <HomePage />;
  }
  if (path === '/pos-management/purchase-orders/create') {
    return routeKeys('purchase_orders.create') ? <PurchaseOrderCreatePage /> : <HomePage />;
  }
  // A warehouse opens the orders placed with it on the same page (read-only).
  const poEditMatch = path.match(/^\/pos-management\/purchase-orders\/([^/]+)\/edit$/);
  if (poEditMatch) {
    return routeKeys('purchase_orders.view', 'sales_orders.view') ? (
      <PurchaseOrderEditPage orderId={poEditMatch[1]} />
    ) : (
      <HomePage />
    );
  }
  const poPrintMatch = path.match(/^\/pos-management\/purchase-orders\/([^/]+)\/print$/);
  if (poPrintMatch) {
    return routeKeys('purchase_orders.view') ? <PurchaseOrderPrintPage orderId={poPrintMatch[1]} /> : <HomePage />;
  }
  // Consolidated packing list print view — PO ids arrive via the hash query
  // (?loc=…&ids=…), which useHashRoute strips from the path.
  if (path === '/pos-management/packing/print') {
    return routeKeys('sales_orders.packing') ? <PackingConsolidatedPrintPage /> : <HomePage />;
  }
  const saleOrderEditMatch = path.match(/^\/pos-management\/sale-orders\/([^/]+)\/edit$/);
  if (saleOrderEditMatch) {
    return routeKeys('sales_orders.view') ? <SaleOrderEditPage orderId={saleOrderEditMatch[1]} /> : <HomePage />;
  }
  // Sale Order pick-list print; Building / Aisle filters ride in the hash query.
  const saleOrderPrintMatch = path.match(/^\/pos-management\/sale-orders\/([^/]+)\/print$/);
  if (saleOrderPrintMatch) {
    return routeKeys('sales_orders.view') ? <SaleOrderPrintPage orderId={saleOrderPrintMatch[1]} /> : <HomePage />;
  }
  // Order receipts are printed from the POS reports.
  const orderReceiptMatch = path.match(/^\/reports\/orders\/([^/]+)\/print$/);
  if (orderReceiptMatch) {
    return perms.has('reports.pos.view') ? <OrderReceiptPrintPage orderId={orderReceiptMatch[1]} /> : <HomePage />;
  }
  if (path === '/reports') {
    if (!reportsEnabled || !perms.canOpen('reports')) return <HomePage />;
    return <ReportsPage />;
  }
  if (path === '/statements') {
    // Hiding the nav item alone would leave the page reachable by URL, so the
    // same module gate guards the route. Falls back to Home like the key check.
    if (!perms.canOpen('statements') || !statementsEnabled) return <HomePage />;
    return <StatementsPage />;
  }
  if (path === '/reconciliation') return perms.canOpen('reconciliation') ? <ReconciliationPage /> : <HomePage />;
  if (path === '/funding-transactions') {
    return perms.canOpen('transactions') ? <FundingTransactionsPage /> : <HomePage />;
  }
  if (path === '/esl') {
    // Gated here as well as in the sidebar — hiding the nav entry would still
    // leave the page reachable by typing the URL.
    if (!perms.canOpen('shelfLabels') || !eslEnabled) return <HomePage />;
    return <EslPage />;
  }

  return <PlaceholderPage title="Page Not Found" description="The page you're looking for doesn't exist." />;
}

function ProtectedApp() {
  const isLoading = useAuthCheck();
  const { isAuthenticated, user } = useAuthStore();
  const path = useHashRoute();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-sm text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    window.location.hash = '#/login'; // eslint-disable-line
    return null;
  }

  const isSuperAdmin = user?.role === 'super_admin';
  const router = isSuperAdmin ? <AdminRouter /> : <TenantRouter />;

  // Print views must render bare: AppShell is h-screen/overflow-hidden with a
  // scrollable <main>, so anything longer than one viewport gets clipped in
  // the browser's print output (only the visible portion prints).
  const content = path.endsWith('/print') ? router : <AppShell>{router}</AppShell>;

  return (
    <>
      {/* Keep the app-wide timezone singleton in sync with the selected
          location's configured zone. Tenant users only — super admins operate
          cross-tenant without a single active location. */}
      {!isSuperAdmin && <TimezoneSync />}
      {content}
    </>
  );
}

function AuthGate() {
  const path = useHashRoute();

  // Dev-only component showcase (compiled out of production builds). Serves as
  // the DatePicker's Storybook stand-in — see pages/dev/date-picker-demo.
  if (import.meta.env.DEV && path === '/datepicker-demo') return <DatePickerDemo />;

  // Public auth routes
  if (path === '/login') return <LoginPage />;
  if (path === '/signup') return <SignupPage />;
  if (path === '/admin/login') return <AdminLoginPage />;
  if (path === '/forgot-password') return <ForgotPasswordPage />;
  if (path.startsWith('/reset-password')) return <ResetPasswordPage />;

  // Public customer payment page — no auth required
  const payMatch = path.match(/^\/pay\/([^/]+)$/);
  if (payMatch) return <PayByLinkPage publicToken={payMatch[1]} />;

  // All other routes require authentication
  return <ProtectedApp />;
}

export function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthGate />
        <Toaster />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
